import { StringKey } from '../lang/index';
import { Company } from './company.types';
import { Country } from './country.types';
import { SharePlanType } from './pool-plans.types';
import { User } from './user.types';
import { VestingTask } from './vesting-tasks.types';

export enum StakeholderRole {
  ADMIN = 'admin',
  CONTRIBUTOR = 'contributor',
  STAKEHOLDER = 'stakeholder',
}

export enum StakeholderType {
  ADVISOR = 'advisor',
  ANGEL = 'angel',
  BOARD_MEMBERS = 'board-members',
  EMPLOYEE = 'employee',
  FAMILY_OFFICE = 'family-office',
  FOUNDER = 'founder',
  INVESTMENT_BANKS = 'investment-banks',
  OTHER_INST = 'other-inst',
  PES = 'pes',
  SPV = 'spv',
  VCS = 'vcs',
}

export const stakeholderTypeTitle: Record<StakeholderType, StringKey> = {
  [StakeholderType.ADVISOR]: 'ADVISOR',
  [StakeholderType.ANGEL]: 'ANGEL',
  [StakeholderType.EMPLOYEE]: 'EMPLOYEE',
  [StakeholderType.FOUNDER]: 'FOUNDER',
  [StakeholderType.BOARD_MEMBERS]: 'BOARD_MEMBER',
  [StakeholderType.VCS]: 'VENTURE_CAPITAL',
  [StakeholderType.INVESTMENT_BANKS]: 'INVESTMENT_BANK',
  [StakeholderType.PES]: 'PRIVATE_EQUITY',
  [StakeholderType.FAMILY_OFFICE]: 'FAMILY_OFFICE',
  [StakeholderType.SPV]: 'SPV',
  [StakeholderType.OTHER_INST]: 'OTHER_INST',
};

export const stakeholderSingularTypeTitle: Record<StakeholderType, StringKey> = {
  [StakeholderType.ADVISOR]: 'ADVISOR',
  [StakeholderType.ANGEL]: 'ANGEL',
  [StakeholderType.EMPLOYEE]: 'EMPLOYEE',
  [StakeholderType.FOUNDER]: 'FOUNDER',
  [StakeholderType.BOARD_MEMBERS]: 'BOARD_MEMBER',
  [StakeholderType.VCS]: 'VCS',
  [StakeholderType.INVESTMENT_BANKS]: 'INVESTMENT_BANK',
  [StakeholderType.PES]: 'PRIVATE_EQUITY',
  [StakeholderType.FAMILY_OFFICE]: 'FAMILY_OFFICE',
  [StakeholderType.SPV]: 'SPV',
  [StakeholderType.OTHER_INST]: 'OTHER_INST',
};

export const stakeholderExTypeTitle: Record<StakeholderType, string> = {
  [StakeholderType.ADVISOR]: 'Ex-Advisor',
  [StakeholderType.ANGEL]: '',
  [StakeholderType.EMPLOYEE]: 'Ex-Employee',
  [StakeholderType.FOUNDER]: 'Ex-Founder',
  [StakeholderType.BOARD_MEMBERS]: '',
  [StakeholderType.VCS]: '',
  [StakeholderType.INVESTMENT_BANKS]: '',
  [StakeholderType.PES]: '',
  [StakeholderType.FAMILY_OFFICE]: '',
  [StakeholderType.SPV]: '',
  [StakeholderType.OTHER_INST]: '',
};

export const b2bStakeholderTypes: StakeholderType[] = [
  StakeholderType.ANGEL,
  StakeholderType.FOUNDER,
  StakeholderType.ADVISOR,
  StakeholderType.EMPLOYEE,
  StakeholderType.BOARD_MEMBERS,
];
export const b2cStakeholderTypes: StakeholderType[] = [
  StakeholderType.VCS,
  StakeholderType.INVESTMENT_BANKS,
  StakeholderType.PES,
  StakeholderType.FAMILY_OFFICE,
  StakeholderType.SPV,
  StakeholderType.OTHER_INST,
];

export const itemsToFetchMap = [5, 10, 20, 50, 100];

export enum StakeholderTypeGroup {
  COMPANY = 'company',
  HUMAN = 'human',
}

export enum StakeholderOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export enum AccessRole {
  AUDITOR = 'AUDITOR',
  CONTRIBUTOR = 'CONTRIBUTOR',
}

export const accessRoleTranslationKey: Record<AccessRole, StringKey> = {
  [AccessRole.AUDITOR]: 'AUDITOR',
  [AccessRole.CONTRIBUTOR]: 'CONTRIBUTOR',
};

export const accessRoleTranslationKeyMap = Object.entries(accessRoleTranslationKey);

export enum StakeholderSortBy {
  FULL_NAME = 'fullName',
  LAST_ACTIVE = 'lastActive',
  TYPE = 'type',
  VOTING_RIGHTS = 'voting-rights',
}

export interface Stakeholder {
  accessRole: AccessRole | null;
  additionalNotes?: string;
  address?: string;
  birthday?: string;
  capitalChangeAccess: boolean;
  capTableAccess: boolean;
  company?: Company;
  companyTableAccess: boolean;
  countryOfInc?: Country;
  createdAt: string;
  email: string;
  filesLinks: string[];
  fullName: string;
  hasPendingSignatures?: boolean;
  hasShares?: boolean;
  id: string;
  incDate?: string;
  invitations?: Record<string, boolean>;
  isAccessRevoked: boolean;
  isActive: boolean;
  isInvited: boolean;
  isTerminated: boolean;
  lastActive: string;
  lastVestingDate?: string;
  nationalityCountry?: Country;
  passportExpDate?: string;
  passportNo?: string;
  phone?: string;
  phoneCountryIso2?: string;
  positionAccess: boolean;
  role: StakeholderRole;
  stakeholderCompanyId?: string;
  subEmails?: string[];
  terminationCause?: TerminationCause;
  terminationDate?: string;
  type: StakeholderType;
  users: User[];
  vestingTasks?: VestingTask[];
  votingRightsPercentage?: number;
}

export type PlanStakeholder = {
  cashSettled: number;
  exercisable: number;
  exercised: number;
  expired: number;
  granted: number;
  returnedToPool: number;
  sharesVested: number;
  totalGrantVested: number;
} & Stakeholder;

export enum StakeholderFilter {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export type FilterVariant = 'all' | StakeholderFilter;

export const filterVariant: Record<FilterVariant, StringKey> = {
  all: 'FILTER_ALL',
  [StakeholderFilter.ACTIVE]: 'FILTER_ACTIVE',
  [StakeholderFilter.INACTIVE]: 'FILTER_INACTIVE',
};

export const filterVariantMap = Object.entries(filterVariant);

export type HumanStakeholderDto = {
  additionalNotes?: string;
  address?: string;
  birthday?: string;
  capitalChangeAccess?: boolean;
  capTableAccess?: boolean;
  companyTableAccess?: boolean;
  email: string;
  filesLinks: string[];
  fullName: string;
  nationality?: string;
  passportExpDate?: string;
  passportNo?: string;
  phone?: string;
  phoneCountryIso2?: string;
  positionAccess?: boolean;
  type: StakeholderType;
};

export type CompanyStakeholderDto = {
  additionalNotes?: string;
  address?: string;
  capitalChangeAccess?: boolean;
  capTableAccess?: boolean;
  companyTableAccess?: boolean;
  countryOfInc?: string;
  email: string;
  filesLinks: string[];
  fullName: string;
  incDate?: string;
  phone?: string;
  phoneCountryIso2?: string;
  positionAccess?: boolean;
  stakeholderCompanyId?: string;
  subEmails?: string[];
  type: StakeholderType;
};

export enum StakeholderBulkAction {
  CANCEL_INVITATION = 'cancel-invitation',
  DELETE = 'delete',
  INVITE = 'invite',
  TOGGLE_ACCESS = 'toggle-access',
  TOGGLE_CONTRIBUTOR = 'toggle-contributor',
}

export type StakeholderOwnershipSummary = {
  companyOwned: number;
  investment: number;
  numberOfShares: number;
  roi: number;
  sharePrice: number;
  valueOfShares: number;
  votingPercentage: number;
};

export type StakeholderConsolidatedPositionShareClass = {
  converted: number;
  convertedPercentage: number;
  diluted: number;
  dilutedPercentage: number;
  id: string;
  investment: number;
  issued: number;
  issuedPercentage: number;
  name: string;
  votingPercentage: number;
};

export type StakeholderConsolidatedPosition = {
  shareClasses: StakeholderConsolidatedPositionShareClass[];
};

export type InvestmentSummary = {
  averageSharePrice: number;
  avgValuationChange: number;
  companyTotalRounds: number;
  currentValuationValue: number;
  equityChange: number;
  latestSharePrice: number;
  stakeholderAverageInvestment: number;
  stakeholderEstimatedValue: number;
  stakeholderTotalInvestment: number;
  stakeholderTotalRounds: number;
  totalAmountRaise: number;
};

export type StakeholderBulkActionDto = {
  stakeholderIds: string[];
};

export enum TerminationCause {
  DEATH = 'death',
  DISABILITY = 'disability',
  RESIGNATION = 'resignation',
  RETIREMENT = 'retirement',
  TERMINATION = 'termination',
  TERMINATION_WITH_CAUSE = 'termination-with-cause',
}

export const TerminationCauseTitle: Record<TerminationCause, StringKey> = {
  [TerminationCause.RESIGNATION]: 'RESIGNATION',
  [TerminationCause.TERMINATION]: 'TERMINATION',
  [TerminationCause.TERMINATION_WITH_CAUSE]: 'TERMINATION_WITH_CAUSE',
  [TerminationCause.RETIREMENT]: 'RETIREMENT',
  [TerminationCause.DEATH]: 'DEATH',
  [TerminationCause.DISABILITY]: 'DISABILITY',
};

export type TerminateGrantItemDto = {
  exercisedCount: null | number;
  finalExerciseDate?: string;
  id: string;
  numbersOfGrants: null | number;
  planType: SharePlanType;
  unexercisedSharesCount: null | number;
  unvestedSharesCount: null | number;
  vestedSharesCount: null | number;
};

/**
 * One reverse-vested issuance row of the stakeholder, named by its issuance / company incorporation
 * item id. Omitting either figure takes the row's default: no acceleration, and the par value or
 * share price of the event the row belongs to.
 */
export type TerminateReverseVestingItemDto = {
  acceleratedShares?: number;
  id: string;
  repurchasePrice?: number;
};

export type TerminateStakeholderItemDto = {
  grantItems?: TerminateGrantItemDto[];
  id: string;
  /**
   * True when the operator picked `lastVestingDate`. A derived one is a grant trigger, so the
   * backend then evaluates the reverse-vesting repurchase at the termination date instead.
   */
  isLastVestingDateManual?: boolean;
  lastVestingDate?: string;
  reverseVestingItems?: TerminateReverseVestingItemDto[];
  terminationCause: TerminationCause;
  terminationDate: string;
};

export type TerminateStakeholderDto = {
  sendEmails: boolean;
  stakeholders: TerminateStakeholderItemDto[];
};
